import { z } from 'zod';
import { onlyDigits } from '@functions/brazil-documents';
import { PersonAddress } from './services/responses/person.response';

type Translate = (key: string) => string;

/// Os campos do bloco de endereço como o formulário guarda; servem à pessoa e à empresa.
export interface AddressFormValues {
    postalCode?: string;
    streetName?: string;
    number?: string;
    complement?: string;
    district?: string;
    city?: string;
    state?: string | null;
}

export const ADDRESS_FIELDS = [
    'postalCode', 'streetName', 'number', 'complement', 'district', 'city', 'state',
] as const;

export const EMPTY_ADDRESS: AddressFormValues = {
    postalCode: '', streetName: '', number: '', complement: '', district: '', city: '', state: '',
};

export const addressSchema = {
    postalCode: z.string().optional(),
    streetName: z.string().max(200).optional(),
    number: z.string().max(20).optional(),
    complement: z.string().max(100).optional(),
    district: z.string().max(100).optional(),
    city: z.string().max(100).optional(),
    state: z.string().nullable().optional(),
};

/// Endereço é opcional, mas pela metade não serve (rua sem cidade): começou, completa o básico.
export const refineAddress = (t: Translate) => (data: AddressFormValues, ctx: z.RefinementCtx) => {
    const started = [data.postalCode, data.streetName, data.city, data.state]
        .some((value) => (value ?? '').trim() !== '');

    if (!started) return;

    if (onlyDigits(data.postalCode).length !== 8)
        ctx.addIssue({ code: 'custom', path: ['postalCode'], message: t('people.cepIncompleto') });
    if (!data.streetName?.trim())
        ctx.addIssue({ code: 'custom', path: ['streetName'], message: t('people.informeARua') });
    if (!data.city?.trim())
        ctx.addIssue({ code: 'custom', path: ['city'], message: t('people.informeACidade') });
    if (!data.state)
        ctx.addIssue({ code: 'custom', path: ['state'], message: t('people.escolhaOEstado') });
};

export const addressToForm = (address?: PersonAddress | null): AddressFormValues => ({
    postalCode: address?.postalCode ?? '',
    streetName: address?.streetName ?? '',
    number: address?.number ?? '',
    complement: address?.complement ?? '',
    district: address?.district ?? '',
    city: address?.city ?? '',
    state: address ? String(address.state) : '',
});

/// Nulo tira o endereço: sem CEP completo não há endereço a gravar.
export const addressFromForm = (data: AddressFormValues): PersonAddress | null =>
    onlyDigits(data.postalCode).length === 8
        ? {
            postalCode: onlyDigits(data.postalCode),
            streetName: data.streetName?.trim() ?? '',
            number: data.number?.trim() || null,
            complement: data.complement?.trim() || null,
            district: data.district?.trim() || null,
            city: data.city?.trim() ?? '',
            state: Number(data.state),
        }
        : null;
